import type { HTMLMotionProps } from 'motion/react';
import styled from 'styled-components';

import {
  Z_INDEX_HEADER_SEARCH,
  Z_INDEX_OVERLAY,
} from '@savgroup-front-common/constants';
import {
  darken,
  rgba,
  setLightness,
} from '@savgroup-front-common/primitive/helpers/cssColor';
import { spacing } from '@savgroup-front-common/primitive/helpers/spacing';

import { SafeMotionDiv } from '../../animation/SafeMotionDiv';
import { Button } from '../button';

import {
  DIRECTION,
  DRAWER_PADDING_SCALE,
  DRAWER_SIZE,
} from './SwipeableDrawer.constants';

export const $Backdrop = styled.div<{ $opacity: number }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${({ theme, $opacity }) =>
    rgba(darken(0.47, setLightness(0.5, theme.colors.primary)), $opacity)};
  z-index: ${Z_INDEX_OVERLAY};
`;

type SheetProps = HTMLMotionProps<'div'> & {
  direction?: DIRECTION;
  size?: DRAWER_SIZE;
  $hasPadding: boolean;
  $hasScroll: boolean;
  $hasBackdrop: boolean;
  $isMobileView: boolean;
  $windowHeight: number;
  $windowWidth: number;
};

export const $Sheet = styled(SafeMotionDiv)<
  React.PropsWithChildren<SheetProps>
>`
  z-index: ${Z_INDEX_OVERLAY};
  position: fixed;

  border: ${({ theme, $hasBackdrop }) =>
    $hasBackdrop ? 'none' : `1px solid ${theme.colors.default}`};

  top: ${({ direction }) => {
    if (direction === DIRECTION.RIGHT) {
      return 0;
    }

    return direction === DIRECTION.TOP ? 0 : undefined;
  }};
  bottom: ${({ direction }) => {
    if (direction === DIRECTION.RIGHT) {
      return undefined;
    }

    return direction === DIRECTION.TOP ? undefined : 0;
  }};
  left: ${({ direction }) => {
    if (direction === DIRECTION.RIGHT) {
      return 0;
    }

    return direction === DIRECTION.TOP ? 0 : undefined;
  }};
  right: ${({ direction }) => {
    if (direction === DIRECTION.RIGHT) {
      return undefined;
    }

    return direction === DIRECTION.TOP ? undefined : 0;
  }};

  height: ${({ direction, size, $windowHeight }) => {
    if (size === DRAWER_SIZE.FULL_SCREEN) {
      return `${$windowHeight}px`;
    }

    return direction === DIRECTION.TOP ? 'calc(100vh + 100px)' : '100vh';
  }};
  width: ${({ direction, size, $isMobileView, $windowWidth }) => {
    if (direction === DIRECTION.TOP) {
      return $isMobileView ? `${$windowWidth}px` : '100%';
    }

    if (
      size !== DRAWER_SIZE.X_SMALL &&
      size !== DRAWER_SIZE.TINY &&
      size !== DRAWER_SIZE.SMALL &&
      size !== DRAWER_SIZE.MEDIUM &&
      size !== DRAWER_SIZE.LARGE
    ) {
      return `calc(100vw - ${spacing(7)})`;
    }

    if (size === DRAWER_SIZE.LARGE) {
      return '75%';
    }

    if (size) {
      return `calc(${size}px + 25px)`;
    }

    return 'calc(100vh + 100px)';
  }};

  border-radius: ${({ direction }) =>
    direction === DIRECTION.TOP ? '12px 12px 0' : '0 0 0 0'};
  background-color: ${({ theme }) => theme.systemColors.defaultBackground};

  & header {
    touch-action: none;
  }

  padding: ${({ $hasPadding }) => {
    return $hasPadding ? spacing(DRAWER_PADDING_SCALE) : '0';
  }};

  overflow: ${({ $hasScroll }) => ($hasScroll ? 'auto' : 'hidden')};

  transition: 0.2s width ease-in-out;
  display: flex;
  flex-direction: column;
`;

export const $DragHandle = styled.div`
  position: absolute;
  top: 0.5rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: ${Z_INDEX_HEADER_SEARCH};
  width: 36px;
  height: 4px;
  border-radius: 2px;
  background-color: ${({ theme }) => theme.newUI.variationColors.light5};
`;

export const $CloseButton = styled(Button)`
  position: absolute;
  top: ${spacing(11)};
  right: ${spacing('xxl')};
  transform: translateY(-50%);
  z-index: ${Z_INDEX_HEADER_SEARCH};
`;

export const $SwipeableDrawerSeparator = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.newUI.variationColors.light5};
  width: 110%;
  margin: ${spacing('lg')} calc(-1 * ${spacing('xl')});
`;
